import { useTranslation } from 'react-i18next'

import { COUNTERPARTY_CATEGORIES } from '@/features/counterparties/counterpartyList'
import { cn } from '@/lib/cn'
import type { CounterpartyCategory } from '@/types/domain'

/* The list page's categories as a row of chips with counts, «Все» first and
 * chosen by default (Locked visual decision, `list-a-chips.png`).
 *
 * One chip at a time, so `aria-pressed` buttons in a labelled group rather than
 * a checkbox list. The chosen chip is drawn in ink, never blue: blue is the
 * page's one primary action, and a filter is not one. The chosen state is also
 * a heavier border — a shape, so colour never carries it alone. The row wraps
 * on a phone; it never scrolls sideways. */
export function CounterpartyCategoryChips({
  value,
  counts,
  onChange,
}: {
  value: CounterpartyCategory | 'all'
  counts: Record<CounterpartyCategory | 'all', number>
  onChange: (next: CounterpartyCategory | 'all') => void
}) {
  const { t } = useTranslation()
  const chips: Array<CounterpartyCategory | 'all'> = ['all', ...COUNTERPARTY_CATEGORIES]

  return (
    <div role="group" aria-label={t('counterparties.chipsLabel')} className="flex flex-wrap gap-1.5">
      {chips.map((chip) => {
        const chosen = chip === value
        return (
          <button
            key={chip}
            type="button"
            aria-pressed={chosen}
            onClick={() => onChange(chip)}
            className={cn(
              'bg-surface inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-3 text-[13px]',
              'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
              chosen
                ? 'border-text-heading text-text-heading border-[1.5px]'
                : 'border-border-default text-text-body hover:bg-surface-hover border',
            )}
          >
            <span>{t(`counterparties.chips.${chip}`)}</span>
            <span className={cn('asa-num', chosen ? 'text-text-heading' : 'text-text-muted')}>
              {counts[chip]}
            </span>
          </button>
        )
      })}
    </div>
  )
}
